/**
 * Keycloak Configuration & Singleton Instance
 *
 * Direct integration with idp.collabkare.id using keycloak-js adapter.
 * Uses Authorization Code Flow with PKCE (most secure for SPAs).
 *
 * Keycloak Setup Requirements:
 * - Realm: CollabKare
 * - Client ID: my-collabkare-id (public client, no secret)
 * - Standard Flow: Enabled
 * - Direct Access Grants: Disabled
 * - Valid Redirect URIs: https://my.collabkare.id/*, http://localhost:3000/*
 * - Web Origins: https://my.collabkare.id, http://localhost:3000
 */

import Keycloak from "keycloak-js";

// Keycloak configuration from environment variables
const keycloakConfig = {
  url: import.meta.env.VITE_KEYCLOAK_URL || "https://idp.collabkare.id",
  realm: import.meta.env.VITE_KEYCLOAK_REALM || "CollabKare",
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || "my-collabkare-id",
};

// Singleton Keycloak instance
const keycloak = new Keycloak(keycloakConfig);

// Token refresh interval (refresh 60 seconds before expiry)
const TOKEN_MIN_VALIDITY_SECONDS = 60;
let refreshIntervalId: ReturnType<typeof setInterval> | null = null;

/**
 * Initialize Keycloak with check-sso (silent check if user is already logged in).
 * Does NOT force login — allows public pages to load without auth.
 */
export async function initKeycloak(): Promise<boolean> {
  try {
    // Only use silent SSO redirect on private routes.
    // Public pages (/, /start, /login, /signup, etc.) skip the iframe
    // to avoid CSP frame-ancestors errors from the Keycloak server.
    const publicPaths = ["/", "/start", "/login", "/signup", "/setupuser",
      "/forgotpassword", "/privacyPolicy", "/terms", "/bookdemo",
      "/Registration", "/UpdatePassword"];
    const isPublicPage = publicPaths.includes(window.location.pathname);

    const authenticated = await keycloak.init({
      onLoad: "check-sso",
      pkceMethod: "S256",
      checkLoginIframe: false,
      ...(isPublicPage
        ? {} // No silent SSO on public pages — avoids iframe CSP error
        : { silentCheckSsoRedirectUri: window.location.origin + "/silent-check-sso.html" }),
    });

    if (authenticated) {
      startTokenRefresh();
    }

    // Listen for token expiry
    keycloak.onTokenExpired = () => {
      keycloak
        .updateToken(TOKEN_MIN_VALIDITY_SECONDS)
        .then((refreshed) => {
          if (!refreshed) {
            console.warn("Token not refreshed, session may have expired");
          }
        })
        .catch(() => {
          console.error("Failed to refresh token, logging out");
          keycloak.logout();
        });
    };

    // Listen for auth errors
    keycloak.onAuthError = (error) => {
      console.error("Keycloak auth error:", error);
    };

    // Listen for logout
    keycloak.onAuthLogout = () => {
      stopTokenRefresh();
    };

    return authenticated;
  } catch (error) {
    console.error("Keycloak initialization failed:", error);
    return false;
  }
}

/**
 * Trigger Keycloak login redirect.
 * User will be redirected to Keycloak's hosted login page.
 */
export function login(redirectUri?: string) {
  keycloak.login({
    redirectUri: redirectUri || window.location.origin + "/dashboard",
  });
}

/**
 * Trigger Keycloak registration redirect.
 * User will be redirected to Keycloak's registration page.
 */
export function register(redirectUri?: string) {
  keycloak.register({
    redirectUri: redirectUri || window.location.origin + "/dashboard",
  });
}

/**
 * Logout the user and redirect to the start page.
 */
export function logout(redirectUri?: string) {
  stopTokenRefresh();
  sessionStorage.clear();
  keycloak.logout({
    redirectUri: redirectUri || window.location.origin + "/start",
  });
}

/**
 * Get the current access token, refreshing if needed.
 * Use this in API interceptors.
 */
export async function getToken(): Promise<string | undefined> {
  try {
    await keycloak.updateToken(TOKEN_MIN_VALIDITY_SECONDS);
    return keycloak.token;
  } catch {
    // Token refresh failed — user needs to re-login
    login();
    return undefined;
  }
}

/**
 * Get the parsed token (JWT claims).
 */
export function getTokenParsed() {
  return keycloak.tokenParsed;
}

/**
 * Get user profile info from the token.
 */
export function getUserProfile() {
  const token = keycloak.tokenParsed;
  if (!token) return null;

  return {
    id: token.sub,
    email: token.email as string | undefined,
    emailVerified: token.email_verified as boolean | undefined,
    firstName: token.given_name as string | undefined,
    lastName: token.family_name as string | undefined,
    fullName: token.name as string | undefined,
    preferredUsername: token.preferred_username as string | undefined,
    // Custom CollabKare attributes (set in Keycloak user attributes / client mappers)
    phone: (token as Record<string, unknown>).phone_number as string | undefined,
    tenantAssociations: (token as Record<string, unknown>).tenant_associations as string[] | undefined,
    verificationLevel: (token as Record<string, unknown>).verification_level as number | undefined,
    roles: keycloak.realmAccess?.roles || [],
    // Client-specific roles from resource_access (Step 8: App access from KC roles)
    resourceAccess: keycloak.resourceAccess || {},
  };
}

/**
 * Get the resource_access claim — maps client IDs to their granted roles.
 * Used to determine which CollabKare apps the user can access.
 */
export function getResourceAccess(): Record<string, { roles: string[] }> {
  return keycloak.resourceAccess || {};
}

/**
 * Check if user is currently authenticated.
 */
export function isAuthenticated(): boolean {
  return !!keycloak.authenticated;
}

/**
 * Check if user has a specific realm role.
 */
export function hasRole(role: string): boolean {
  return keycloak.hasRealmRole(role);
}

/**
 * Start periodic token refresh.
 */
function startTokenRefresh() {
  stopTokenRefresh();
  // Refresh token every 4 minutes (tokens typically expire in 5 min)
  refreshIntervalId = setInterval(async () => {
    try {
      await keycloak.updateToken(TOKEN_MIN_VALIDITY_SECONDS);
    } catch {
      console.warn("Token refresh failed during interval");
    }
  }, 4 * 60 * 1000);
}

/**
 * Stop periodic token refresh.
 */
function stopTokenRefresh() {
  if (refreshIntervalId) {
    clearInterval(refreshIntervalId);
    refreshIntervalId = null;
  }
}

// Export the keycloak instance for advanced use cases
export default keycloak;
